import { queryOptions } from '@tanstack/react-query'

import { fetchJson, path } from '@/api/client'
import { keys } from '@/api/keys'
import type { AnnotationKind, CharacterBody, Lint, ObjectBody, PublishedTier, ReviewStatus, StageBody } from '@/schema'

/**
 * `/api/annotations/*` (§5.3).
 *
 * Two rules the whole file is built around:
 *
 *  1. **Uids go into the path verbatim.** `char:people/royalty/king/king_asian`
 *     has three slashes and a colon and is never `encodeURIComponent`d
 *     (§5 preamble) — hence `path\`\`` from `api/client.ts` everywhere below.
 *  2. **A schema-valid body always saves.** `PUT` answers `200` with the
 *     derived `lints[]` whatever their severity; only a stale `base_rev`
 *     (`409`) or a schema violation (`422`) fails. Error lints block
 *     *publish* and *reviewed*, never the save (§4.3.4).
 */

export type AnnotationBody = StageBody | CharacterBody | ObjectBody

/** §4.2 `Envelope<K, B>` as it arrives on the wire. */
export interface AnnotationEnvelope<B extends AnnotationBody = AnnotationBody> {
  schema: `story-studio.${AnnotationKind}/1`
  asset: { uid: string; key: string; etag?: string; kind: 'background' | 'live_background' | 'character' | 'object' | 'audio' }
  rev: number
  status: ReviewStatus
  published_tier: PublishedTier | null
  published_rev: number | null
  vocab_rev: number
  provenance: { source: 'seed' | 'heuristic' | 'ai' | 'human'; confidence: number; at: string; by: string; job_id?: number; locked?: boolean }
  body: B
}

export type StageEnvelope = AnnotationEnvelope<StageBody>

export interface RevisionRow {
  rev: number
  parent_rev: number | null
  source: 'seed' | 'heuristic' | 'ai' | 'human' | 'restore' | 'publish' | 'snapshot'
  author: string
  job_id?: number | null
  proposal_id?: number | null
  summary?: string | null
  created_at: string
  lint_errors: number
}

/** `annotation.kind` for an asset kind; `audio` has no annotation document (404 by design). */
export function annotationKindFromSchema(schema: string): AnnotationKind | null {
  const m = /^story-studio\.(stage|character|object)\/1$/.exec(schema)
  return m ? (m[1] as AnnotationKind) : null
}

/* -------------------------------------------------------------------------- */
/* Reads                                                                       */
/* -------------------------------------------------------------------------- */

/** `GET /api/annotations/{uid}` — the draft head, or a snapshot with `rev`. */
export function getAnnotation(uid: string, rev?: number, signal?: AbortSignal): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}`, { query: rev === undefined ? undefined : { rev }, signal })
}

export function listRevisions(uid: string, signal?: AbortSignal): Promise<{ items: RevisionRow[] }> {
  return fetchJson<{ items: RevisionRow[] }>(path`/api/annotations/${uid}/revisions`, { signal })
}

export function getRevision(uid: string, rev: number, signal?: AbortSignal): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}/revisions/${rev}`, { signal })
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * How a save resolves a proposal the reviewer merged **locally** (explicit-Save model).
 * `accepted_ids` / `rejected_ids` are the PROPOSAL's feature ids. `status` may be left out:
 * the server derives it from `accepted_ids` (none → rejected, all → accepted, some → partial).
 */
export interface ProposalResolution {
  id: number
  status?: 'accepted' | 'partial' | 'rejected'
  accepted_ids?: string[]
  rejected_ids?: string[]
}

export interface PutAnnotationRequest {
  base_rev: number
  body: AnnotationBody
  /** Single-proposal shorthand; merged with `resolved_proposals` by the server. */
  proposal?: ProposalResolution
  /** Proposals whose items this save contains (max 20). A `409` leaves them all untouched. */
  resolved_proposals?: ProposalResolution[]
}

/**
 * `PUT /api/annotations/{uid}`. Resolves with the new envelope (`rev` bumped,
 * `body.lints[]` derived by the server). Rejects with `ApiError`:
 * `409` carries `details.current_rev`, `422` means the client sent a body the
 * JSON schema refuses — a bug, never a user mistake. Nothing calls this on a timer:
 * the editor saves only when the person presses Save (or Ctrl+S, or "Save and leave").
 */
export function putAnnotation(uid: string, req: PutAnnotationRequest): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}`, { method: 'PUT', body: req })
}

export function restoreRevision(uid: string, rev: number): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}/revisions/${rev}/restore`, { method: 'POST' })
}

export function snapshotAnnotation(uid: string, summary?: string): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}/snapshot`, { method: 'POST', body: { summary } })
}

/** `422` when `lint_errors > 0`; `details.lints[]` carries the messages and fix hints. */
export function publishAnnotation(uid: string, tier: 'reviewed' = 'reviewed'): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}/publish`, { method: 'POST', body: { tier } })
}

export function unpublishAnnotation(uid: string): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}/unpublish`, { method: 'POST' })
}

/** `draft` always succeeds; `reviewed` is `422` while `lint_errors > 0`. */
export function setAnnotationStatus(uid: string, status: 'draft' | 'reviewed'): Promise<AnnotationEnvelope> {
  return fetchJson<AnnotationEnvelope>(path`/api/annotations/${uid}/status`, { method: 'POST', body: { status } })
}

/** Dry-run lint of a body the editor has not saved yet. */
export function lintAnnotation(uid: string, body: AnnotationBody, signal?: AbortSignal): Promise<{ lints: Lint[]; affordances: string[] }> {
  return fetchJson<{ lints: Lint[]; affordances: string[] }>(path`/api/annotations/${uid}/lint`, { method: 'POST', body: { body }, signal })
}

export type CopyToSkinsField = 'points' | 'height_cm' | 'defaults' | 'clips'

export function copyToSkins(uid: string, fields: CopyToSkinsField[]): Promise<{ updated: string[] }> {
  return fetchJson<{ updated: string[] }>(path`/api/annotations/${uid}/copy-to-skins`, { method: 'POST', body: { fields } })
}

/* -------------------------------------------------------------------------- */
/* Query options                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The document the editor loads. `staleTime: Infinity` on purpose: the
 * editor owns the in-memory copy from the moment it mounts and a background
 * refetch would silently fight the operator's edits. Freshness comes from
 * the `doc.updated` SSE event, which invalidates this key explicitly.
 */
export const annotationQuery = (uid: string, rev?: number) =>
  queryOptions({
    queryKey: keys.annotation.doc(uid, rev),
    queryFn: ({ signal }) => getAnnotation(uid, rev, signal),
    staleTime: Infinity,
    enabled: uid.length > 0,
    retry: (count, err) => count < 2 && !(err instanceof Error && 'status' in err && (err as { status: number }).status === 404),
  })

export const revisionsQuery = (uid: string) =>
  queryOptions({
    queryKey: keys.annotation.revisions(uid),
    queryFn: ({ signal }) => listRevisions(uid, signal),
    staleTime: 10_000,
    enabled: uid.length > 0,
  })

/* -------------------------------------------------------------------------- */
/* Lint helpers shared by the editor panels                                    */
/* -------------------------------------------------------------------------- */

export const countLints = (lints: readonly Lint[] | undefined): { errors: number; warnings: number; infos: number } => {
  let errors = 0
  let warnings = 0
  let infos = 0
  for (const l of lints ?? []) {
    if (l.severity === 'error') errors++
    else if (l.severity === 'warn') warnings++
    else infos++
  }
  return { errors, warnings, infos }
}

/** Lints that point at one feature id, for the per-shape warning badge. */
export function lintsByRef(lints: readonly Lint[] | undefined): Map<string, Lint[]> {
  const map = new Map<string, Lint[]>()
  for (const l of lints ?? []) {
    if (!l.ref) continue
    const list = map.get(l.ref)
    if (list) list.push(l)
    else map.set(l.ref, [l])
  }
  return map
}
